import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAddedExam, getTemplateDetails, getToBeRemoved } from '../../templates-selector';
import styled from 'styled-components';
import { Button, Typography } from 'antd';
import { setAddedExam, setCanAddExam, updateTemplateDetails } from '../../templates-actions';
import { getTemplateEventColor } from '../../../../common/helpers/common';
import { ConfigTypes } from '../../models/config-types.enum';
import { NewExamination } from './configurations/NewExamination';
import { Lunch } from './configurations/Lunch';
import { on } from 'events';

const { Title } = Typography;

// --- Pace calculation utility ---
function calculatePace(slots, details) {
  if (!slots || slots.length === 0) return 0;
  // Exclude slots with examName 'Lunch' (case-insensitive)
  const filteredSlots = slots.filter((s) => {
    if (!s?.examName) return true;
    return s?.examName.toLowerCase() !== 'lunch';
  });
  // Parse 12-hour AM/PM time string to minutes
  function parseAmPmTime(str) {
    if (!str) return NaN;
    const match = str.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (!match) return NaN;
    let [, h, m, period] = match;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
    if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  const startTime = details?.startAt;
  const endTime = details?.endAt;
  const startMins = parseAmPmTime(startTime);
  const endMins = parseAmPmTime(endTime);
  let hours = (endMins - startMins) / 60;
  if (isNaN(hours) || hours <= 0) hours = 1;
  const pace = filteredSlots.length / hours;
  return Number(pace.toFixed(1));
}

type NewConfigurationProps = {
  data: any;
  onClosed: any;
  isEdit?: boolean;
  onInserted?: (exam: any, isEdit?: boolean) => void;
  isSpecialRange?: boolean;
  isRange0?: boolean;
};

export const NewConfiguration = ({
  data,
  onClosed,
  isEdit = false,
  onInserted = () => {},
  isSpecialRange = false,
  isRange0 = false,
}: NewConfigurationProps) => {
  const dispatch = useDispatch();
  const newExam = useSelector(getAddedExam); // Always use latest from Redux
  const toBeRemoved = useSelector(getToBeRemoved);
  const [examName, setExamName] = useState(isEdit ? data?.examName?.toLowerCase() : '');
  const templateDetails = useSelector((state) => getTemplateDetails(state));

  const getConfiguration = (name) => {
    switch (name) {
      case ConfigTypes.Exam:
      case ConfigTypes.OfficeUseOnly:
      case ConfigTypes.DispenseRepair:
      case ConfigTypes.ExamCl:
      case ConfigTypes.TExam:
      case ConfigTypes.TExamCL:
      case ConfigTypes.Flex:
      case ConfigTypes.Lunch:
      case ConfigTypes.RcOv: {
        return (
          <NewExamination
            data={isEdit ? data : newExam}
            onInsert={handleInsertExam}
            onClose={handleCloseNewExam}
            isEdit={isEdit}
            isSpecialRange={isSpecialRange}
            isRange0={isRange0}
          />
        );
      }
      // case ConfigTypes.Lunch:
      //   return <Lunch data={newExam} />;
      default:
        return null;
    }
  };

  // Handler to close NewExamination without insert
  const handleCloseNewExam = () => {
    onClosed(isEdit);
    //setShowNewExam(false);
    dispatch(setCanAddExam(false));
    dispatch(setAddedExam(null)); // Clear addedExam in Redux

    // if a slot was previously removed for editing, restore it back to templateDetails
    if (toBeRemoved && isEdit) {
      const latestDetails = templateDetails || {};
      const currentExams = Array.isArray(latestDetails.examsConfiguration) ? latestDetails.examsConfiguration : [];

      // don't duplicate if already present
      const exists = currentExams.some((e) => e.examId === toBeRemoved.examId);
      if (!exists) {
        const restoredExams = [...currentExams, toBeRemoved];

        // Recalculate examsNameCount
        const nameCountMap: Record<string, number> = {};
        restoredExams.forEach((exam) => {
          const name = exam?.examName;
          if (!name) return;
          nameCountMap[name] = (nameCountMap[name] || 0) + 1;
        });
        const examsNameCount = Object.entries(nameCountMap).map(([examName, count]) => ({ examName, count }));

        // Recalculate pace and update template details
        const pace = calculatePace(restoredExams, latestDetails);
        const updatedDetails = {
          ...latestDetails,
          examsConfiguration: restoredExams,
          examsNameCount,
          pace,
        };
        dispatch(updateTemplateDetails(updatedDetails));
      }
    }
  };

  // Handler for Insert in NewExamination
  const handleInsertExam = (examDetails) => {
    const latestDetails = templateDetails || {};
    const currentExams = Array.isArray(latestDetails.examsConfiguration) ? latestDetails.examsConfiguration : [];
    const newExam = {
      ...examDetails,
      examId: examDetails.examId || 0,
    };
    const newExams = [...currentExams, newExam];
    // Recalculate examsNameCount by examName for ExamBar
    const nameCountMap = {};
    newExams?.forEach((exam) => {
      const name = exam?.examName;
      if (!name) return;
      nameCountMap[name] = (nameCountMap[name] || 0) + 1;
    });
    const examsNameCount = Object.entries(nameCountMap).map(([examName, count]) => ({ examName, count }));
    // --- Pace calculation ---
    const pace = calculatePace(newExams, latestDetails);
    const updatedDetails = {
      ...latestDetails,
      examsConfiguration: newExams,
      examsNameCount,
      pace,
    };
    dispatch(updateTemplateDetails(updatedDetails));
    // Notify parent that insertion/update completed in this panel so it can
    // mark session-only highlights (if needed).
    try {
      onInserted(newExam, isEdit);
    } catch (e) {
      // ignore
    }
    onClosed(isEdit);
    dispatch(setCanAddExam(false));
    dispatch(setAddedExam(null));
  };

  useEffect(() => {
    if (newExam) {
      setExamName(newExam?.examName?.toLowerCase?.() || '');
    }
  }, [newExam]);

  return (
    <StyledConfigWrapper>
      <StyledHeader>
        <Title level={5}>{`${examName} configuration`.toUpperCase()} </Title>
        <CloseButton type={'link'} onClick={handleCloseNewExam}>
          <img src={'./icons/close-thin.svg'} alt='close' />
        </CloseButton>
      </StyledHeader>
      {getConfiguration(examName)}
    </StyledConfigWrapper>
  );
};

const StyledConfigWrapper = styled.div`
  padding: 15px 20px;
  border: 3px solid ${({ title }) => getTemplateEventColor(title)};
  border-radius: 3px;
  min-height: 420px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const CloseButton = styled(Button)`
  padding: 0;
  line-height: 0;
  height: 20px;
`;
